import type { FormField } from '@lucid/lego';
import type { FieldValues } from 'react-hook-form';

export type TechSupportFormCallbacks = {
  onSave?: (data: FieldValues) => void;
  onCancel?: () => void;
};

export type VehicleFormData = {
  vin?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
};

export const createTechSupportFormFields = (
  callbacks?: TechSupportFormCallbacks,
  vehicleData?: VehicleFormData
): FormField[] => [
  {
    id: 'caseBasicsGroup',
    fieldType: 'group',
    title: 'Case Basics',
    width: 'full',
    children: [
      {
        id: 'caseType',
        fieldType: 'dropdown',
        name: 'caseType',
        apiKey: 'caseType',
        label: 'Type',
        placeholder: 'Select one',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'Type is required',
        },
        options: [
          { label: 'Software', value: 'software' },
          { label: 'Technical Assistance', value: 'technical_assistance' },
        ],
      },
      {
        id: 'createdDatetime',
        fieldType: 'date_picker',
        name: 'createdDatetime',
        apiKey: 'createdDatetime',
        label: 'Time Stamp',
        placeholder: new Date().toISOString(),
        width: 'half',
        size: 'medium',
        locale: 'en-US',
      },
      {
        id: 'priority',
        fieldType: 'dropdown',
        name: 'priority',
        apiKey: 'priority',
        label: 'Priority',
        placeholder: 'Select one',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'Priority is required',
        },
        options: [
          { label: 'Diagnostic Tool', value: 'diagnostic_tool' },
          { label: 'Info Only', value: 'info_only' },
          { label: 'Critical', value: 'critical' },
        ],
      },
      {
        id: 'componentList',
        fieldType: 'dropdown',
        name: 'componentList',
        apiKey: 'componentList',
        label: 'Components',
        placeholder: 'Select all that apply',
        required: true,
        width: 'half',
        multiSelect: true,
        validationMessages: {
          required: 'At least one component is required',
        },
        options: [
          { label: 'ADAS', value: 'adas' },
          { label: 'Powertrain', value: 'powertrain' },
          { label: 'Electrical HV/LV', value: 'electrical_hv_lv' },
          { label: 'Brakes', value: 'brakes' },
          { label: 'Body', value: 'body' },
          { label: 'Interior', value: 'interior' },
          { label: 'Lighting', value: 'lighting' },
          { label: 'Advanced Vehicle Electronics', value: 'advanced_vehicle_electronics' },
          { label: 'Passive Safety', value: 'passive_safety' },
          { label: 'NVH', value: 'nvh' },
          { label: 'Wheels & Tires', value: 'wheels_tires' },
          { label: 'Other', value: 'other' },
        ],
      },
      {
        id: 'description',
        fieldType: 'textarea',
        name: 'description',
        apiKey: 'description',
        label: 'Description',
        placeholder: 'List the actions performed',
        required: true,
        width: 'full',
        rows: 5,
        maxLength: 5000,
        showCharCount: true,
        resize: 'vertical',
        validationMessages: {
          required: 'Description is required',
        },
      },
    ],
  },
  {
    id: 'safetyEscalationGroup',
    fieldType: 'group',
    title: 'Safety Escalation Concern & Verified?',
    width: 'full',
    children: [
      {
        id: 'safetyCheckVerificationList',
        fieldType: 'checkbox_group',
        name: 'safetyCheckVerificationList',
        apiKey: 'safetyCheckVerificationList',
        width: 'full',
        layout: 'horizontal',
        options: [
          { value: 'authorizationMissing', label: 'Authorization missing' },
          { value: 'causalPartNumberMissing', label: 'Causal part number missing/incorrect' },
          { value: 'causeOutOfCompliance', label: 'Cause out of compliance' },
          { value: 'concernOutOfCompliance', label: 'Concern out of compliance' },
        ],
      },
    ],
  },
  {
    id: 'customerGroup',
    fieldType: 'group',
    title: 'Customer',
    width: 'full',
    children: [
      {
        id: 'customerName',
        fieldType: 'text',
        name: 'customerName',
        apiKey: 'customerName',
        label: 'Name',
        placeholder: 'Tom Smith',
        defaultValue: vehicleData?.customerName || '',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'Name is required',
        },
      },
      {
        id: 'vin',
        fieldType: 'text',
        name: 'vin',
        apiKey: 'vin',
        label: 'VIN',
        placeholder: 'WLCID1TEA0PA00444',
        defaultValue: vehicleData?.vin || '',
        required: true,
        width: 'half',
        pattern: /^[A-HJ-NPR-Z0-9]{17}$/i,
        validationMessages: {
          required: 'VIN is required',
          pattern:
            'VIN must be exactly 17 alphanumeric characters (letters I, O, Q are not allowed)',
        },
      },
      {
        id: 'customerPhone',
        fieldType: 'phone',
        name: 'customerPhone',
        apiKey: 'customerPhone',
        label: 'Phone Number',
        value: vehicleData?.customerPhone || '',
        width: 'half',
        validationMessages: {
          required: 'Phone number is required',
        },
      },
      {
        id: 'customerEmail',
        fieldType: 'email',
        name: 'customerEmail',
        apiKey: 'customerEmail',
        label: 'Email',
        placeholder: 'tom.smith@gmail.com',
        defaultValue: vehicleData?.customerEmail || '',
        width: 'half',
      },
    ],
  },
  {
    id: 'workOrderGroup',
    fieldType: 'group',
    title: 'Work Order / Repair Order',
    width: 'full',
    children: [
      {
        id: 'workOrderNumber',
        fieldType: 'text',
        name: 'workOrderNumber',
        apiKey: 'workOrderNumber',
        label: 'Work Order / Repair Order Number',
        placeholder: 'Add',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'Work Order / Repair Order number is required',
        },
      },
      {
        id: 'workOrderJobCode',
        fieldType: 'text',
        name: 'workOrderJobCode',
        apiKey: 'workOrderJobCode',
        label: 'Work Order Jobcode (If Warranty Case)',
        placeholder: 'Add',
        width: 'half',
      },
      {
        id: 'woRoJobLines',
        fieldType: 'text',
        name: 'woRoJobLines',
        apiKey: 'woRoJobLines',
        label: 'WO/RO Line(s) Escalated',
        placeholder: 'Add',
        width: 'half',
      },
      {
        id: 'mileageInMiles',
        fieldType: 'number',
        name: 'mileageInMiles',
        apiKey: 'mileageInMiles',
        label: 'Mileage In',
        placeholder: 'Add',
        width: 'half',
        pattern: /^[0-9]+$/,
        validationMessages: {
          pattern: 'Please enter whole numbers only',
        },
      },
      {
        id: 'customerConcern',
        fieldType: 'textarea',
        name: 'customerConcern',
        apiKey: 'customerConcern',
        label: 'Customer Concern (From WO/RO)',
        placeholder: 'Auto copied from WO / WOJC',
        required: true,
        width: 'full',
        rows: 5,
        maxLength: 5000,
        showCharCount: true,
        resize: 'vertical',
      },
    ],
  },
  {
    id: 'technicianDealerGroup',
    fieldType: 'group',
    title: 'Technician / Dealer Information',
    width: 'full',
    children: [
      {
        id: 'technicianName',
        fieldType: 'text',
        name: 'technicianName',
        apiKey: 'technicianName',
        label: 'Technician Name',
        placeholder: 'Add',
        width: 'half',
      },
      {
        id: 'contactNameForEscalation',
        fieldType: 'text',
        name: 'contactNameForEscalation',
        apiKey: 'contactNameForEscalation',
        label: 'Contact Name (If Different)',
        placeholder: 'Add',
        width: 'half',
      },
      {
        id: 'importerArLocation',
        fieldType: 'text',
        name: 'importerArLocation',
        apiKey: 'importerArLocation',
        label: 'Importer/AR Location',
        placeholder: 'Stuttgart',
        width: 'half',
      },
    ],
  },
  {
    id: 'repairServiceGroup',
    fieldType: 'group',
    title: 'Repair / Service Details',
    width: 'full',
    children: [
      {
        id: 'mobileRepairOrServiceRepair',
        fieldType: 'dropdown',
        name: 'mobileRepairOrServiceRepair',
        apiKey: 'mobileRepairOrServiceRepair',
        label: 'Mobile Repair or Service Repair',
        placeholder: 'Mobile Repair / Service Repair',
        width: 'half',
        options: [
          { label: 'Mobile Repair', value: 'mobile_repair' },
          { label: 'Service Repair', value: 'service_repair' },
        ],
      },
      {
        id: 'isVehicleOnsite',
        fieldType: 'dropdown',
        name: 'isVehicleOnsite',
        apiKey: 'isVehicleOnsite',
        label: 'Vehicle Onsite',
        placeholder: 'Yes / No',
        width: 'half',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
      {
        id: 'mobileServiceVisitAtVehicleLocation',
        fieldType: 'dropdown',
        name: 'mobileServiceVisitAtVehicleLocation',
        apiKey: 'mobileServiceVisitAtVehicleLocation',
        label: 'Mobile Service Visit at Vehicle Location',
        placeholder: 'On Site / Not on Site',
        width: 'half',
        options: [
          { label: 'On Site', value: 'on_site' },
          { label: 'Not on Site', value: 'not_on_site' },
        ],
      },
      {
        id: 'isVehicleRecovered',
        fieldType: 'dropdown',
        name: 'isVehicleRecovered',
        apiKey: 'isVehicleRecovered',
        label: 'Vehicle Recovered',
        placeholder: 'Yes / No',
        width: 'half',
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
      },
    ],
  },
  {
    id: 'faultDataGroup',
    fieldType: 'group',
    title: 'Fault Data',
    width: 'full',
    children: [
      {
        id: 'faultCode',
        fieldType: 'text',
        name: 'faultCode',
        apiKey: 'faultCode',
        label: 'Fault Code',
        placeholder: 'Enter associated fault or diagnostic code',
        width: 'half',
        pattern: /^[a-zA-Z0-9-]+$/,
        validationMessages: {
          pattern: 'Please enter alphanumeric characters only',
        },
      },
      {
        id: 'numberOfVisit',
        fieldType: 'dropdown',
        name: 'numberOfVisit',
        apiKey: 'numberOfVisits',
        label: 'Number of Visits',
        placeholder: 'Select no of workshop visits',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'Number of workshop visits is required',
        },
        options: [
          { label: '1', value: '1' },
          { label: '2', value: '2' },
          { label: '3', value: '3' },
          { label: '4', value: '4' },
          { label: '5', value: '5' },
          { label: '6', value: '6' },
          { label: '7', value: '7' },
          { label: '8', value: '8' },
          { label: '9', value: '9' },
          { label: '10', value: '10' },
        ],
      },
    ],
  },
  {
    id: 'attachmentsGroup',
    fieldType: 'group',
    title: 'Attachments',
    width: 'full',
    children: [
      {
        id: 'attachments',
        fieldType: 'fileUpload',
        name: 'attachments',
        apiKey: 'attachments',
        label: 'Case Attachments',
        helperText: 'JPG, PNG, GIF, PDF, DOC files supported (max 50 MB each)',
        dropzoneText: 'Drag and drop files here, or click to browse',
        accept: '.jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.log',
        maxFileSize: 50 * 1024 * 1024,
        multiple: true,
        required: false,
        uploadContext: 'tech-support-case',
      },
    ],
  },
  {
    id: 'formButtons',
    fieldType: 'buttonGroup',
    buttons: [
      {
        id: 'save',
        label: 'Save',
        color: 'neutral',
        size: 'medium',
        type: 'secondary',
        position: 'left',
        onClick: callbacks?.onSave,
      },
      {
        id: 'cancel',
        label: 'Cancel',
        color: 'neutral',
        size: 'medium',
        type: 'secondary',
        position: 'right',
        onClick: callbacks?.onCancel ? () => callbacks.onCancel?.() : undefined,
      },
      {
        id: 'submit',
        label: 'Create Case',
        color: 'neutral',
        size: 'medium',
        type: 'primary',
        position: 'right',
        // No onClick - uses default form submit behavior
      },
    ],
  },
];
